import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import { SettingsAnchorService } from '@configuration/shared/settings-search/settings-anchor.service';
import {
  ISettingsSearchContext,
  ISettingsSearchResult
} from '@configuration/shared/settings-search/settings-search.interfaces';
import {
  segmentsToHtml,
  SettingsSearchService
} from '@configuration/shared/settings-search/settings-search.service';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

/**
 * Command-palette style search over the settings registry. Opened by the
 * settings shell (search bar / Ctrl+K) through `BsModalService.show`, with
 * the firm context passed as `initialState`.
 */
@Component({
  selector: 'app-modals-settings-search',
  templateUrl: './modals.settings-search.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ModalsSettingsSearchComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('searchInput', { static: true })
  public searchInput: ElementRef<HTMLInputElement>;

  /** Provided through `initialState`. */
  public context: ISettingsSearchContext;

  public query = String('');
  public results: ISettingsSearchResult[] = [];
  public activeIndex = Number(0);

  public readonly listboxId = String('settings-search-results');
  public readonly maxResults = SettingsSearchService.MAX_RESULTS;

  private shownSubscription: Subscription;

  constructor(
    private readonly _anchors: SettingsAnchorService,
    private readonly _modalRef: BsModalRef,
    private readonly _modals: BsModalService,
    private readonly _router: Router,
    private readonly _search: SettingsSearchService
  ) {}

  ngOnInit(): void {
    // ngx-bootstrap moves focus to the modal container once it is shown,
    // which happens after ngAfterViewInit, so reclaim it for the input then.
    this.shownSubscription = this._modals.onShown.subscribe(() =>
      this.focusInput()
    );
  }

  ngAfterViewInit(): void {
    this.focusInput();
  }

  ngOnDestroy(): void {
    this.shownSubscription?.unsubscribe();
  }

  public focusInput(): void {
    this.searchInput?.nativeElement.focus();
  }

  public titleHtml(result: ISettingsSearchResult): string {
    return segmentsToHtml(result.titleSegments);
  }

  public get activeOptionId(): string | null {
    return this.results.length > 0 ? this.optionId(this.activeIndex) : null;
  }

  public get isCapped(): boolean {
    return this.results.length >= this.maxResults;
  }

  public optionId(index: number): string {
    return `settings-search-option-${index}`;
  }

  public trackByResult(result: ISettingsSearchResult): string {
    return `${result.entry.tab}#${result.entry.id}`;
  }

  public onQueryChange(query: string): void {
    this.query = query ?? '';
    this.results = this._search.search(this.query, this.context);
    this.activeIndex = 0;
  }

  public onKeydown(event: KeyboardEvent): void {
    const count = this.results.length;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (count > 0) {
          this.activeIndex = (this.activeIndex + 1) % count;
        }
        break;

      case 'ArrowUp':
        event.preventDefault();
        if (count > 0) {
          this.activeIndex = (this.activeIndex - 1 + count) % count;
        }
        break;

      case 'Home':
        if (count > 0) {
          event.preventDefault();
          this.activeIndex = 0;
        }
        break;

      case 'End':
        if (count > 0) {
          event.preventDefault();
          this.activeIndex = count - 1;
        }
        break;

      case 'Enter':
        event.preventDefault();
        if (count > 0) {
          this.select(this.results[this.activeIndex]);
        }
        break;

      default:
        break;
    }
  }

  public select(result: ISettingsSearchResult): void {
    const url = `/settings/${result.tab.url}`;
    const target = `${url}#${result.entry.id}`;

    // ngx-bootstrap hands focus back to the search trigger (in the page
    // header) on a timer once the modal is hidden, and that focus() scrolls
    // the trigger into view. Revealing the card before then gets scrolled
    // and focused right back out, so wait until the modal is fully gone.
    this.afterHidden(() => {
      if (this._router.url === target) {
        // Same tab and same setting: the router treats it as a no-op, so the
        // fragment subscription in the settings shell never fires again.
        this._anchors.reveal(result.entry.id);
      } else {
        this._router.navigate([url], { fragment: result.entry.id });
      }
    });

    this.close();
  }

  public close(): void {
    this._modalRef.hide();
  }

  private afterHidden(callback: () => void): void {
    const onHidden = this._modalRef.onHidden;

    if (onHidden) {
      onHidden.pipe(take(1)).subscribe(callback);
    } else {
      callback();
    }
  }
}
